// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useRef, useEffect } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../types/wizard';
import type { PolicyTypeKey } from '../../types/wizard';
import { toIsoDate } from '../../utils/date';

/** Strip everything except digits and dot, keep only first dot */
function toRawNumber(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

/** Format a raw numeric string as 1,234.56 for display (without € symbol) */
function formatEuro(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

/** On blur, ensure 2 decimal places */
function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return num.toFixed(2);
}

const PENSION_TYPE_OPTIONS = [
  { value: 'PRB_BOB', text: 'PRB / Buy-out Bond' },
  { value: 'PRSA_STANDARD', text: 'PRSA (Standard)' },
  { value: 'PRSA_NON_STANDARD', text: 'PRSA (Non-Standard)' },
  { value: 'PRSA_AVC', text: 'PRSA AVC' },
  { value: 'PERSONAL_PENSION', text: 'Personal Pension' },
  { value: 'DEFINED_CONTRIBUTION', text: 'Defined Contribution' },
  { value: 'EXECUTIVE_PENSION', text: 'Executive Pension' },
  { value: 'MASTERTRUST', text: 'Mastertrust' },
  { value: 'DIRECTORS_PENSION', text: "Director's Pension" },
  { value: 'ARF', text: 'ARF (Approved Retirement Fund)' },
  { value: 'VESTED_PRSA', text: 'Vested PRSA' },
];

const INVESTMENT_TYPE_OPTIONS = [
  { value: 'INVESTMENT_BOND', text: 'Investment Bond' },
  { value: 'REGULAR_SAVER', text: 'Regular Saver' },
  { value: 'CHILDS_SAVER', text: "Child's Saver" },
  { value: 'TRADING_ACCOUNTS', text: 'Trading Accounts' },
  { value: 'INVESTMENT_PORTFOLIO', text: 'Investment Portfolio' },
];

const policyDetailsSchema = yup.object({
  policyType: yup.string().required('Policy type is required'),
  fullPolicyNumber: yup.string().trim().required('Policy number is required'),
  currentValue: yup.string().trim().required('Current value is required'),
  currentValueDate: yup.string().required('Date as at current value is required'),
  transferValue: yup.string().trim().required('Transfer value is required'),
  transferValueDate: yup.string().required('Date as at transfer value is required'),
});

function validateField(fieldName: string, data: Record<string, unknown>): string | undefined {
  try {
    policyDetailsSchema.validateSyncAt(fieldName, data);
    return undefined;
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
    return undefined;
  }
}

// ========================================================================
// Investment field visibility matrix
// ========================================================================
const OWNERSHIP_OPTIONS = [
  { value: 'BARE_TRUST', text: 'Bare Trust' },
  { value: 'TRADING_ACCOUNTS', text: 'Trading Accounts' },
  { value: 'INVESTMENT_PORTFOLIO', text: 'Investment Portfolio' },
  { value: 'STRUCTURED_PRODUCTS', text: 'Structured Products' },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'EXECUTION_ONLY', text: 'Execution Only' },
  { value: 'ADVISORY', text: 'Advisory' },
  { value: 'DISCRETIONARY', text: 'Discretionary' },
];

const PAYING_STATUS_OPTIONS = [
  { value: 'IN_FORCE', text: 'In Force' },
  { value: 'PAID_UP', text: 'Paid Up' },
];

const PREMIUM_FREQUENCY_OPTIONS = [
  { value: 'MONTHLY', text: 'Monthly' },
  { value: 'ANNUALLY', text: 'Annually' },
  { value: 'ADHOC', text: 'Adhoc' },
];


interface StepPolicyDetailsProps extends StepComponentProps {
  policyTypeCategory?: string | null;
  onPolicyTypeChange?: (policyType: PolicyTypeKey | null) => void;
}

export function StepPolicyDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  policyTypeCategory,
  onPolicyTypeChange,
}: StepPolicyDetailsProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [showPolicyNumberInfo, setShowPolicyNumberInfo] = useState(false);

  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT';
  const typeOptions = isInvestment ? INVESTMENT_TYPE_OPTIONS : PENSION_TYPE_OPTIONS;
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPolicyNumberInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowPolicyNumberInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPolicyNumberInfo]);

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data as Record<string, unknown>);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    // Allow only valid partial numeric input (digits, single dot, decimals)
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) {
      onFieldChange(field, formatEuroBlur(raw));
    }
    handleBlur(field);
  }, [data, onFieldChange, handleBlur]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatEuro(raw) : '';
  };

  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  const addIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.6663 8.5H5.33301C5.05967 8.5 4.83301 8.27333 4.83301 8C4.83301 7.72667 5.05967 7.5 5.33301 7.5H10.6663C10.9397 7.5 11.1663 7.72667 11.1663 8C11.1663 8.27333 10.9397 8.5 10.6663 8.5Z" fill="#2D0A6C"/>
      <path d="M8 11.1668V5.3335C8 5.06016 8.22667 4.8335 8.5 4.8335C8.77333 4.8335 9 5.06016 9 5.3335V11.1668C9 11.4402 8.77333 11.6668 8.5 11.6668C8.22667 11.6668 8 11.4402 8 11.1668Z" fill="#2D0A6C" transform="translate(-0.5 -0.5)"/>
      <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" stroke="#2D0A6C"/>
    </svg>
  );

  // ========================================================================
  // Investment flow — all fields always visible
  // ========================================================================
  if (policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
    const names = (data.namesOfLifeAssured as string[]) || [''];

    const handleChangeName = (index: number, value: string) => {
      const updated = [...names];
      updated[index] = value;
      onFieldChange('namesOfLifeAssured', updated);
    };
    const handleAddName = () => onFieldChange('namesOfLifeAssured', [...names, '']);
    const handleRemoveName = (index: number) => {
      const updated = names.filter((_, i) => i !== index);
      onFieldChange('namesOfLifeAssured', updated.length > 0 ? updated : ['']);
    };

    return (
      <div className="flex flex-col gap-lg">
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Policy details
        </div>

        {/* Row 1: Policy number* | Type of Ownership */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="flex items-center justify-between gap-sm">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Policy number / Plan Reference No / Member Reference No <span className="text-[#E7000B]">*</span>
                </div>
                <div className="relative shrink-0" ref={infoRef}>
                  <button
                    type="button"
                    onClick={() => setShowPolicyNumberInfo((prev) => !prev)}
                    className="cursor-pointer bg-transparent border-none p-0 flex items-center"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <mask id="mask_info_inv" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                        <path d="M20 0H0V20H20V0Z" fill="white"/>
                      </mask>
                      <g mask="url(#mask_info_inv)">
                        <path d="M9.99967 1.04183C14.9413 1.04183 18.958 5.0585 18.958 10.0002C18.958 14.9418 14.9413 18.9585 9.99967 18.9585C5.05801 18.9585 1.04134 14.9418 1.04134 10.0002C1.04134 5.0585 5.05801 1.04183 9.99967 1.04183ZM9.99967 17.7085C14.2497 17.7085 17.708 14.2502 17.708 10.0002C17.708 5.75016 14.2497 2.29183 9.99967 2.29183C5.74967 2.29183 2.29134 5.75016 2.29134 10.0002C2.29134 14.2502 5.74967 17.7085 9.99967 17.7085Z" fill="#141414"/>
                        <path d="M10 8.54183C10.3417 8.54183 10.625 8.82516 10.625 9.16683V13.3335C10.625 13.6752 10.3417 13.9585 10 13.9585C9.65833 13.9585 9.375 13.6752 9.375 13.3335V9.16683C9.375 8.82516 9.65833 8.54183 10 8.54183Z" fill="#141414"/>
                        <path d="M9.99967 5.83262C10.108 5.83262 10.2163 5.85762 10.3163 5.89928C10.4163 5.94095 10.508 5.99928 10.5913 6.07428C10.6663 6.15762 10.7247 6.24095 10.7663 6.34928C10.808 6.44928 10.833 6.55762 10.833 6.66595C10.833 6.77428 10.808 6.88262 10.7663 6.98262C10.7247 7.08262 10.6663 7.17428 10.5913 7.25762C10.508 7.33262 10.4163 7.39095 10.3163 7.43262C10.1163 7.51595 9.88301 7.51595 9.68301 7.43262C9.58301 7.39095 9.49134 7.33262 9.40801 7.25762C9.33301 7.17428 9.27467 7.08262 9.23301 6.98262C9.19134 6.88262 9.16634 6.77428 9.16634 6.66595C9.16634 6.55762 9.19134 6.44928 9.23301 6.34928C9.27467 6.24095 9.33301 6.15762 9.40801 6.07428C9.49134 5.99928 9.58301 5.94095 9.68301 5.89928C9.78301 5.85762 9.89134 5.83262 9.99967 5.83262Z" fill="#141414"/>
                      </g>
                    </svg>
                  </button>
                  {showPolicyNumberInfo && (
                    <div className="absolute right-0 top-[28px] z-10 w-[280px] px-[12px] py-sm rounded-lg bg-black text-white font-sans text-[13px] font-normal leading-normal shadow-lg">
                      This number may be referred to by various names depending on the provider
                    </div>
                  )}
                </div>
              </div>
              <input
                type="text"
                placeholder="POL_1234"
                value={(data.fullPolicyNumber as string) || ''}
                onChange={(e) => {
                  onFieldChange('fullPolicyNumber', e.target.value);
                  onClearFieldError('fullPolicyNumber');
                }}
                onBlur={() => handleBlur('fullPolicyNumber')}
                className={`w-full h-[48px] px-md rounded-lg border ${getError('fullPolicyNumber') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
              />
            </div>
            {getError('fullPolicyNumber') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('fullPolicyNumber')}</div>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Type of Ownership
              </div>
              <div className="relative">
                <select
                  value={(data.typeOfOwnership as string) || ''}
                  onChange={(e) => onFieldChange('typeOfOwnership', e.target.value)}
                  className={`w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${(data.typeOfOwnership as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Select...</option>
                  {OWNERSHIP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Investment Type* | Type of Account */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Investment Type <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={(data.policyType as string) || ''}
                  onChange={(e) => {
                    onFieldChange('policyType', e.target.value);
                    onClearFieldError('policyType');
                    onPolicyTypeChange?.(e.target.value ? (e.target.value as PolicyTypeKey) : null);
                  }}
                  onBlur={() => handleBlur('policyType')}
                  className={`w-full h-[48px] px-md pr-xxl rounded-lg border ${getError('policyType') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.policyType as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Select investment type...</option>
                  {INVESTMENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            {getError('policyType') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyType')}</div>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Type of Account
              </div>
              <div className="relative">
                <select
                  value={(data.typeOfAccount as string) || ''}
                  onChange={(e) => onFieldChange('typeOfAccount', e.target.value)}
                  className={`w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${(data.typeOfAccount as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Select...</option>
                  {ACCOUNT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Risk Level | Policy Start Date */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Risk Level
              </div>
              <input
                type="text"
                value={(data.riskLevel as string) || ''}
                onChange={(e) => onFieldChange('riskLevel', e.target.value)}
                className={`w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
              />
            </div>
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Policy Start Date
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.policyStartDate as string) || ''}
                  onChange={(e) => onFieldChange('policyStartDate', toIsoDate(e.target.value))}
                  className={`w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${(data.policyStartDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                />
                {calendarIcon('mask_psd')}
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Amount Invested (bookcost) | Current / Policy value* */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Amount Invested (bookcost)
              </div>
              <div className={`flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary`}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('amountInvested')}
                  onChange={(e) => handleCurrencyChange('amountInvested', e.target.value)}
                  onBlur={() => handleCurrencyBlur('amountInvested')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Current / Policy value <span className="text-[#E7000B]">*</span>
              </div>
              <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('currentPolicyValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('currentPolicyValue')}
                  onChange={(e) => handleCurrencyChange('currentPolicyValue', e.target.value)}
                  onBlur={() => handleCurrencyBlur('currentPolicyValue')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
            </div>
            {getError('currentPolicyValue') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('currentPolicyValue')}</div>
            )}
          </div>
        </div>

        {/* Row 5: Date as at current/policy value* | Exit penalty details* */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Date as at current/ policy value <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.dateCurrentPolicyValue as string) || ''}
                  onBlur={() => {
                    handleBlur('dateCurrentPolicyValue');
                  }}
                  onChange={(e) => {
                    onFieldChange('dateCurrentPolicyValue', toIsoDate(e.target.value));
                    onClearFieldError('dateCurrentPolicyValue');
                  }}
                  className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('dateCurrentPolicyValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.dateCurrentPolicyValue as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                />
                {calendarIcon('mask_dcpv')}
              </div>
            </div>
            {getError('dateCurrentPolicyValue') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dateCurrentPolicyValue')}</div>
            )}
          </div>

          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Exit penalty details <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.exitPenaltyDetails as string) || ''}
                onChange={(e) => {
                  onFieldChange('exitPenaltyDetails', e.target.value);
                  onClearFieldError('exitPenaltyDetails');
                }}
                onBlur={() => handleBlur('exitPenaltyDetails')}
                className={`w-full h-[48px] px-md rounded-lg border ${getError('exitPenaltyDetails') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
              />
            </div>
            {getError('exitPenaltyDetails') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('exitPenaltyDetails')}</div>
            )}
          </div>
        </div>

        {/* Row 6: Exit Taxes Payable on surrender | Transfer/Surrender value* */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Exit Taxes Payable on surrender
              </div>
              <input
                type="text"
                value={(data.exitTaxesPayable as string) || ''}
                onChange={(e) => onFieldChange('exitTaxesPayable', e.target.value)}
                className={`w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
              />
            </div>
          </div>

          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Transfer/Surrender value <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.transferSurrenderValue as string) || ''}
                onChange={(e) => {
                  onFieldChange('transferSurrenderValue', e.target.value);
                  onClearFieldError('transferSurrenderValue');
                }}
                onBlur={() => handleBlur('transferSurrenderValue')}
                className={`w-full h-[48px] px-md rounded-lg border ${getError('transferSurrenderValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
              />
            </div>
            {getError('transferSurrenderValue') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transferSurrenderValue')}</div>
            )}
          </div>
        </div>

        {/* Row 7: Date as at transfer/surrender value* | Maturity Date */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col flex-1 relative">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Date as at transfer/ surrender value <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.dateTransferSurrenderValue as string) || ''}
                  onBlur={() => {
                    handleBlur('dateTransferSurrenderValue');
                  }}
                  onChange={(e) => {
                    onFieldChange('dateTransferSurrenderValue', toIsoDate(e.target.value));
                    onClearFieldError('dateTransferSurrenderValue');
                  }}
                  className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('dateTransferSurrenderValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.dateTransferSurrenderValue as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                />
                {calendarIcon('mask_dtsv')}
              </div>
            </div>
            {getError('dateTransferSurrenderValue') && (
              <div className="absolute left-0 top-full mt-xs text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dateTransferSurrenderValue')}</div>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex flex-col gap-sm flex-1 justify-end">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Maturity Date
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.maturityDate as string) || ''}
                  onChange={(e) => onFieldChange('maturityDate', toIsoDate(e.target.value))}
                  className={`w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${(data.maturityDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                />
                {calendarIcon('mask_md')}
              </div>
            </div>
          </div>
        </div>

        {/* Row 8: Paying status | Premium Frequency */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Paying status
            </div>
            <div className="relative">
              <select
                value={(data.payingStatus as string) || ''}
                onChange={(e) => onFieldChange('payingStatus', e.target.value)}
                className={`w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${(data.payingStatus as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
              >
                <option value="" disabled className="text-[#B6B6B6]">Select...</option>
                {PAYING_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Premium Frequency
            </div>
            <div className="relative">
              <select
                value={(data.premiumFrequency as string) || ''}
                onChange={(e) => onFieldChange('premiumFrequency', e.target.value)}
                className={`w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${(data.premiumFrequency as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
              >
                <option value="" disabled className="text-[#B6B6B6]">Select...</option>
                {PREMIUM_FREQUENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Row 9: Premium Amount | Indexation details */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Premium Amount
            </div>
            <div className={`flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary`}>
              <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
              <input
                type="text"
                inputMode="decimal"
                value={getCurrencyDisplay('premiumAmount')}
                onChange={(e) => handleCurrencyChange('premiumAmount', e.target.value)}
                onBlur={() => handleCurrencyBlur('premiumAmount')}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
            </div>
          </div>

          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Indexation details if applicable
            </div>
            <input
              type="text"
              value={(data.indexationDetails as string) || ''}
              onChange={(e) => onFieldChange('indexationDetails', e.target.value)}
              className={`w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
            />
          </div>
        </div>

        {/* Row 10+: Names of Life/lives assured (2 per row) + Add Another Name */}
        {(() => {
          const rows: number[][] = [];
          for (let i = 0; i < names.length; i += 2) {
            rows.push(i + 1 < names.length ? [i, i + 1] : [i]);
          }
          return (
            <div className="flex flex-col gap-lg">
              {rows.map((pair) => (
                <div key={pair[0]} className="flex flex-row gap-lg">
                  {pair.map((idx) => (
                    <div key={idx} className="flex flex-col gap-sm flex-1">
                      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                        Names of Life/lives assured
                      </div>
                      <div className="flex items-center gap-sm">
                        <input
                          type="text"
                          value={names[idx]}
                          onChange={(e) => handleChangeName(idx, e.target.value)}
                          className={`w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
                        />
                        {names.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveName(idx)}
                            className="cursor-pointer bg-transparent border-none p-0 flex items-center shrink-0"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 4.48665C13.9867 4.48665 13.9667 4.48665 13.9467 4.48665C10.42 4.13332 6.9 3.99999 3.41333 4.35332L2.05333 4.48665C1.77333 4.51332 1.52667 4.31332 1.5 4.03332C1.47333 3.75332 1.67333 3.51332 1.94667 3.48665L3.30667 3.35332C6.85333 2.99332 10.4467 3.13332 14.0467 3.48665C14.32 3.51332 14.52 3.75999 14.4933 4.03332C14.4733 4.29332 14.2533 4.48665 14 4.48665Z" fill="#2D0A6C"/>
                              <path d="M5.66699 3.81334C5.64033 3.81334 5.61366 3.81334 5.58033 3.80668C5.31366 3.76001 5.12699 3.50001 5.17366 3.23334L5.32033 2.36001C5.42699 1.72001 5.57366 0.833344 7.12699 0.833344H8.87366C10.4337 0.833344 10.5803 1.75334 10.6803 2.36668L10.827 3.23334C10.8737 3.50668 10.687 3.76668 10.4203 3.80668C10.147 3.85334 9.88699 3.66668 9.84699 3.40001L9.70033 2.53334C9.60699 1.95334 9.58699 1.84001 8.88033 1.84001H7.13366C6.42699 1.84001 6.41366 1.93334 6.31366 2.52668L6.16033 3.39334C6.12033 3.64001 5.90699 3.81334 5.66699 3.81334Z" fill="#2D0A6C"/>
                              <path d="M10.14 15.1667H5.86004C3.53337 15.1667 3.4267 13.88 3.34004 12.84L2.86004 5.72668C2.84004 5.45334 3.04671 5.21334 3.32004 5.19334C3.60004 5.18001 3.83337 5.38001 3.85337 5.65334L4.33337 12.7667C4.42004 13.7867 4.4467 14.1667 5.86004 14.1667H10.14C11.56 14.1667 11.5867 13.7867 11.6667 12.7667L12.1467 5.65334C12.1667 5.38001 12.4067 5.18001 12.68 5.19334C12.9534 5.21334 13.16 5.44668 13.14 5.72668L12.66 12.84C12.5734 13.88 12.4667 15.1667 10.14 15.1667Z" fill="#2D0A6C"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {pair.length === 1 && <div className="flex-1" />}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddName}
                className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
              >
                {addIcon}
                <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
                  Add Another Name
                </div>
              </button>
            </div>
          );
        })()}
      </div>
    );
  }

  // ========================================================================
  // Pension flow (original layout)
  // ========================================================================
  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Policy details
      </div>

      {/* Row 1: Policy Type* | Policy number* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-sm flex-1 justify-end">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              {isInvestment ? 'Investment Type' : 'Pension Type'} <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={(data.policyType as string) || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onFieldChange('policyType', val);
                  onClearFieldError('policyType');
                  onPolicyTypeChange?.(val ? (val as PolicyTypeKey) : null);
                }}
                onBlur={() => handleBlur('policyType')}
                className={`w-full h-[48px] px-md pr-xxl rounded-lg border ${getError('policyType') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.policyType as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
              >
                <option value="" disabled className="text-[#B6B6B6]">{isInvestment ? 'Select investment type...' : 'Select pension type...'}</option>
                {typeOptions.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          {getError('policyType') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{getError('policyType')}</div>
          )}
        </div>

        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-sm flex-1 justify-end">
            <div className="flex items-center justify-between gap-sm">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Policy number / Plan Reference No / Member Reference No <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative shrink-0" ref={infoRef}>
                <button
                  type="button"
                  onClick={() => setShowPolicyNumberInfo((prev) => !prev)}
                  className="cursor-pointer bg-transparent border-none p-0 flex items-center"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="mask_info_policy" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                      <path d="M20 0H0V20H20V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask_info_policy)">
                      <path d="M9.99967 1.04183C14.9413 1.04183 18.958 5.0585 18.958 10.0002C18.958 14.9418 14.9413 18.9585 9.99967 18.9585C5.05801 18.9585 1.04134 14.9418 1.04134 10.0002C1.04134 5.0585 5.05801 1.04183 9.99967 1.04183ZM9.99967 17.7085C14.2497 17.7085 17.708 14.2502 17.708 10.0002C17.708 5.75016 14.2497 2.29183 9.99967 2.29183C5.74967 2.29183 2.29134 5.75016 2.29134 10.0002C2.29134 14.2502 5.74967 17.7085 9.99967 17.7085Z" fill="#141414"/>
                      <path d="M10 8.54183C10.3417 8.54183 10.625 8.82516 10.625 9.16683V13.3335C10.625 13.6752 10.3417 13.9585 10 13.9585C9.65833 13.9585 9.375 13.6752 9.375 13.3335V9.16683C9.375 8.82516 9.65833 8.54183 10 8.54183Z" fill="#141414"/>
                      <path d="M9.99967 5.83262C10.108 5.83262 10.2163 5.85762 10.3163 5.89928C10.4163 5.94095 10.508 5.99928 10.5913 6.07428C10.6663 6.15762 10.7247 6.24095 10.7663 6.34928C10.808 6.44928 10.833 6.55762 10.833 6.66595C10.833 6.77428 10.808 6.88262 10.7663 6.98262C10.7247 7.08262 10.6663 7.17428 10.5913 7.25762C10.508 7.33262 10.4163 7.39095 10.3163 7.43262C10.1163 7.51595 9.88301 7.51595 9.68301 7.43262C9.58301 7.39095 9.49134 7.33262 9.40801 7.25762C9.33301 7.17428 9.27467 7.08262 9.23301 6.98262C9.19134 6.88262 9.16634 6.77428 9.16634 6.66595C9.16634 6.55762 9.19134 6.44928 9.23301 6.34928C9.27467 6.24095 9.33301 6.15762 9.40801 6.07428C9.49134 5.99928 9.58301 5.94095 9.68301 5.89928C9.78301 5.85762 9.89134 5.83262 9.99967 5.83262Z" fill="#141414"/>
                    </g>
                  </svg>
                </button>
                {showPolicyNumberInfo && (
                  <div className="absolute right-0 top-[28px] z-10 w-[280px] px-[12px] py-sm rounded-lg bg-black text-white font-sans text-[13px] font-normal leading-normal shadow-lg">
                    This number may be referred to by various names depending on the provider
                  </div>
                )}
              </div>
            </div>
            <input
              type="text"
              value={(data.fullPolicyNumber as string) || ''}
              onChange={(e) => {
                onFieldChange('fullPolicyNumber', e.target.value);
                onClearFieldError('fullPolicyNumber');
              }}
              onBlur={() => handleBlur('fullPolicyNumber')}
              className={`w-full h-[48px] px-md rounded-lg border ${getError('fullPolicyNumber') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
            />
          </div>
          {getError('fullPolicyNumber') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{getError('fullPolicyNumber')}</div>
          )}
        </div>
      </div>

      {/* Row 3: Current value* | Date as at current value* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Current value <span className="text-[#E7000B]">*</span>
          </div>
          <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('currentValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('currentValue')}
              onChange={(e) => handleCurrencyChange('currentValue', e.target.value)}
              onBlur={() => handleCurrencyBlur('currentValue')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('currentValue') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('currentValue')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date as at current value <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="date"
              value={(data.currentValueDate as string) || ''}
              onBlur={() => {
                handleBlur('currentValueDate');
              }}
              onChange={(e) => {
                onFieldChange('currentValueDate', toIsoDate(e.target.value));
                onClearFieldError('currentValueDate');
              }}
              className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('currentValueDate') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.currentValueDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
            />
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask_cvd" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <path d="M16 0H0V16H16V0Z" fill="white"/>
              </mask>
              <g mask="url(#mask_cvd)">
                <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
                <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
                <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
                <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
              </g>
            </svg>
          </div>
          {getError('currentValueDate') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('currentValueDate')}</div>
          )}
        </div>
      </div>

      {/* Row 4: Transfer value* | Date as at transfer value* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Transfer value <span className="text-[#E7000B]">*</span>
          </div>
          <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('transferValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('transferValue')}
              onChange={(e) => handleCurrencyChange('transferValue', e.target.value)}
              onBlur={() => handleCurrencyBlur('transferValue')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('transferValue') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transferValue')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date as at transfer value <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="date"
              value={(data.transferValueDate as string) || ''}
              onBlur={() => {
                handleBlur('transferValueDate');
              }}
              onChange={(e) => {
                onFieldChange('transferValueDate', toIsoDate(e.target.value));
                onClearFieldError('transferValueDate');
              }}
              className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('transferValueDate') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.transferValueDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
            />
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask_tvd" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <path d="M16 0H0V16H16V0Z" fill="white"/>
              </mask>
              <g mask="url(#mask_tvd)">
                <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
                <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
                <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
                <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
              </g>
            </svg>
          </div>
          {getError('transferValueDate') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transferValueDate')}</div>
          )}
        </div>
      </div>

      {/* Row 5: Scheme Revenue Reference Number */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Scheme Revenue Reference Number
          </div>
          <input
            type="text"
            value={(data.schemeRevenueRefNumber as string) || ''}
            onChange={(e) => {
              onFieldChange('schemeRevenueRefNumber', e.target.value);
              onClearFieldError('schemeRevenueRefNumber');
            }}
            onBlur={() => handleBlur('schemeRevenueRefNumber')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('schemeRevenueRefNumber') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary`}
          />
          <div className="text-[#1E1E1E] font-sans text-body-03 font-normal leading-normal">
            eg. SF123456 or sometimes PB123456
          </div>
          {getError('schemeRevenueRefNumber') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('schemeRevenueRefNumber')}</div>
          )}
        </div>
        <div className="flex-1" />
      </div>
    </div>
  );
}
